// @ts-nocheck
/**
 * ポートフォリオ管理システム
 * 保有ファンドの一覧表示、分析、履歴管理
 */

import { ALL_FUNDS, getFund, FUND_TYPES } from '../data/funds';
import { getFundPrice, getFundPriceChange, getFundPriceChangeFromBase } from './priceEngine';
import { getHolding, getHoldingValue, getTotalPortfolioValue } from './fundManager';
import { calculateFXPositionPnL } from './fxManager';
import { INVESTMENT } from '../../data/constants';

// ========================================
// ポートフォリオサマリー
// ========================================

/**
 * ポートフォリオのサマリー情報を取得
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} サマリー情報
 */
export function getPortfolioSummary(investmentData, marketIndex = null, marketState = null) {
  // ファンド評価額
  const fundResult = getTotalPortfolioValue(investmentData, marketIndex, marketState);

  // FXポジション評価額
  const fxPositions = investmentData.fxPositions || [];
  let fxTotalCost = 0;     // 証拠金合計
  let fxTotalValue = 0;    // 評価額（証拠金 + 損益 - スワップ）
  let fxTotalPnl = 0;      // 評価損益

  for (const position of fxPositions) {
    // pair フィールドに統一（旧データの pairId もフォールバックでサポート）
    const pairId = position.pair || position.pairId;
    const currentRate = (investmentData.fxRates && investmentData.fxRates[pairId]) || position.entryRate;
    const { pnl } = calculateFXPositionPnL(position, currentRate);
    const swapPaid = position.swapPaid || 0;

    fxTotalCost += position.margin;
    fxTotalPnl += pnl - swapPaid;
    fxTotalValue += position.margin + pnl - swapPaid;
  }

  // 合算
  const totalCost = fundResult.totalCost + fxTotalCost;
  const totalValue = fundResult.totalValue + fxTotalValue;
  const totalPnl = fundResult.totalPnl + fxTotalPnl;
  const totalPnlPercent = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

  // 安全なデフォルト値
  const holdings = investmentData.holdings || [];
  const totalStats = investmentData.totalStats || {
    totalInvestments: 0,
    totalProfitableTrades: 0,
    totalInvestedAmount: 0,
    totalPayout: 0,
    totalProfit: 0,
    totalFeesPaid: 0,
  };

  return {
    // 評価額（ファンド + FX 合算）
    totalValue,
    totalCost,
    totalPnl,
    totalPnlPercent,

    // ファンドのみの情報（参考用）
    fundTotalValue: fundResult.totalValue,
    fundTotalCost: fundResult.totalCost,
    fundTotalPnl: fundResult.totalPnl,

    // FXのみの情報（参考用）
    fxTotalValue,
    fxTotalCost,
    fxTotalPnl,
    fxPositionsCount: fxPositions.length,

    // 保有状況
    holdingsCount: holdings.length,
    totalUnits: holdings.reduce((sum, h) => sum + h.units, 0),

    // コスト
    totalMaintenancePaid: investmentData.totalMaintenancePaid || 0,

    // 統計
    ...totalStats,

    // 経験値
    experienceCount: investmentData.experienceCount || 0,
  };
}

// ========================================
// 保有一覧
// ========================================

/**
 * 保有ファンド一覧を詳細情報付きで取得
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Array} 保有ファンド一覧
 */
export function getHoldingsList(investmentData, currentBattleNumber, marketIndex = null, marketState = null) {
  const holdings = investmentData.holdings || [];
  return holdings.map((holding) => {
    const fund = getFund(holding.fundId);
    // 新シグネチャで呼び出し、フォールバックとして後方互換シグネチャも使用
    const currentPrice = (marketIndex && marketState)
      ? getFundPrice(holding.fundId, marketIndex, marketState)
      : getFundPrice(investmentData, holding.fundId);
    const direction = holding.direction || 'long';

    // ポジション損益計算（ショートは逆）
    let priceDiff;
    if (direction === 'long') {
      priceDiff = currentPrice - holding.purchasePrice;
    } else {
      priceDiff = holding.purchasePrice - currentPrice;
    }
    const pnl = priceDiff * holding.units;
    const cost = holding.purchasePrice * holding.units;
    const margin = holding.margin || cost;
    const value = margin + pnl;  // 証拠金 + 損益
    const pnlPercent = margin > 0 ? (pnl / margin) * 100 : 0;

    // 期限なし（現物）の場合はnull
    const daysRemaining = holding.expiryDate !== null
      ? holding.expiryDate - currentBattleNumber
      : null;
    const priceChange = getFundPriceChange(investmentData, holding.fundId);
    const holdingDays = currentBattleNumber - holding.purchaseDate;

    return {
      ...holding,
      fund,
      currentPrice,
      value,
      cost,
      margin,
      pnl,
      pnlPercent,
      daysRemaining,
      holdingDays,
      priceChange,
      isExpiringSoon: daysRemaining !== null && daysRemaining <= 3,
      isExpired: daysRemaining !== null && daysRemaining <= 0,
      isNoExpiry: holding.expiryDate === null,  // 期限なしフラグ
    };
  });
}

/**
 * 保有一覧をソート
 * @param {Array} holdings - 保有一覧
 * @param {string} sortBy - ソートキー ('value' | 'pnl' | 'pnlPercent' | 'daysRemaining')
 * @param {boolean} ascending - 昇順か
 * @returns {Array} ソート済み一覧
 */
export function sortHoldings(holdings, sortBy = 'value', ascending = false) {
  return [...holdings].sort((a, b) => {
    const valueA = a[sortBy] || 0;
    const valueB = b[sortBy] || 0;
    return ascending ? valueA - valueB : valueB - valueA;
  });
}

// ========================================
// ファンド一覧（購入用）
// ========================================

/**
 * 購入可能なファンド一覧を取得
 * @param {Object} investmentData - 投資データ
 * @param {string} type - ファンドタイプ（'region' | 'meta' | 'category' | null = 全部）
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Array} ファンド一覧
 */
export function getAvailableFunds(investmentData, type = null, marketIndex = null, marketState = null) {
  const funds = type
    ? Object.values(ALL_FUNDS).filter((f) => f.type === type)
    : Object.values(ALL_FUNDS);

  return funds.map((fund) => {
    // 新シグネチャで呼び出し、フォールバックとして後方互換シグネチャも使用
    const currentPrice = (marketIndex && marketState)
      ? getFundPrice(fund.id, marketIndex, marketState)
      : getFundPrice(investmentData, fund.id);
    const priceChange = getFundPriceChange(investmentData, fund.id);
    const priceChangeFromBase = (marketIndex && marketState)
      ? getFundPriceChangeFromBase(fund.id, marketIndex, marketState)
      : getFundPriceChangeFromBase(investmentData, fund.id);
    const holding = getHolding(investmentData, fund.id);

    return {
      ...fund,
      currentPrice,
      priceChange,
      priceChangeFromBase,
      holdingUnits: holding ? holding.units : 0,
      isHolding: !!holding,
      isHot: priceChange >= 5, // 5%以上上昇でHOT
      isCold: priceChange <= -5, // 5%以上下落でCOLD
    };
  });
}

// ========================================
// 取引履歴
// ========================================

/**
 * 取引履歴を取得
 * @param {Object} investmentData - 投資データ
 * @param {Object} options - オプション
 * @returns {Array} 取引履歴
 */
export function getTradeHistory(investmentData, options = {}) {
  const { type, fundId, limit = 50 } = options;

  let history = [...(investmentData.history || [])];

  // タイプでフィルタ
  if (type) {
    history = history.filter((h) => h.type === type);
  }

  // ファンドIDでフィルタ
  if (fundId) {
    history = history.filter((h) => h.fundId === fundId || h.pair === fundId);
  }

  // 新しい順にソート
  history.sort((a, b) => b.date - a.date);

  // 件数制限
  return history.slice(0, limit);
}

/**
 * 取引履歴に詳細情報を付加
 * @param {Array} history - 取引履歴
 * @returns {Array} 詳細付き取引履歴
 */
export function enrichTradeHistory(history) {
  return history.map((entry) => {
    const fund = entry.fundId ? getFund(entry.fundId) : null;

    // FX取引の場合
    if (entry.type === 'fx') {
      const directionText = entry.direction === 'long' ? 'ロング' : 'ショート';
      const actionText = entry.action === 'open_fx' ? `新規${directionText}` : `決済${directionText}`;
      return {
        ...entry,
        fundName: entry.pair || '不明',
        fundIcon: '📈',
        actionText,
        isProfitable: entry.pnl && entry.pnl > 0,
        // FX用の値を設定
        fee: entry.spread ?? 0,
        price: entry.entryRate ?? entry.exitRate ?? 0,
        units: entry.margin ?? 0,  // 証拠金を表示
        date: entry.date ?? 0,
        isFX: true,
        leverage: entry.leverage ?? 1,
      };
    }

    // ファンド取引の場合
    return {
      ...entry,
      fundName: fund ? fund.name : entry.pair || '不明',
      fundIcon: fund ? fund.icon : '📊',
      actionText: entry.action === 'buy' ? '購入' : '売却',
      isProfitable: entry.pnl && entry.pnl > 0,
      // 安全なデフォルト値を設定
      fee: entry.fee ?? 0,
      price: entry.price ?? 0,
      units: entry.units ?? 0,
      date: entry.date ?? 0,
      isFX: false,
    };
  });
}

// ========================================
// 分析
// ========================================

/**
 * ポートフォリオの属性分布を取得
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} 属性 => { value, percent }
 */
export function getAttributeDistribution(investmentData, marketIndex = null, marketState = null) {
  const distribution = {};
  const { totalValue } = getTotalPortfolioValue(investmentData, marketIndex, marketState);
  const holdings = investmentData.holdings || [];

  for (const holding of holdings) {
    const fund = getFund(holding.fundId);
    if (!fund || !fund.attribute) continue;

    // 新シグネチャで呼び出し、フォールバックとして後方互換シグネチャも使用
    const currentPrice = (marketIndex && marketState)
      ? getFundPrice(holding.fundId, marketIndex, marketState)
      : getFundPrice(investmentData, holding.fundId);
    const value = currentPrice * holding.units;

    if (!distribution[fund.attribute]) {
      distribution[fund.attribute] = { value: 0, percent: 0 };
    }
    distribution[fund.attribute].value += value;
  }

  // パーセンテージ計算
  for (const attr of Object.keys(distribution)) {
    distribution[attr].percent = totalValue > 0
      ? (distribution[attr].value / totalValue) * 100
      : 0;
  }

  return distribution;
}

/**
 * ポートフォリオのタイプ分布を取得
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} タイプ => { value, percent }
 */
export function getTypeDistribution(investmentData, marketIndex = null, marketState = null) {
  const distribution = {
    [FUND_TYPES.REGION]: { value: 0, percent: 0, label: '地域ファンド' },
    [FUND_TYPES.META]: { value: 0, percent: 0, label: 'メタファンド' },
    [FUND_TYPES.CATEGORY]: { value: 0, percent: 0, label: 'カテゴリファンド' },
  };
  const { totalValue } = getTotalPortfolioValue(investmentData, marketIndex, marketState);

  for (const holding of investmentData.holdings) {
    const fund = getFund(holding.fundId);
    if (!fund) continue;

    // 新シグネチャで呼び出し、フォールバックとして後方互換シグネチャも使用
    const currentPrice = (marketIndex && marketState)
      ? getFundPrice(holding.fundId, marketIndex, marketState)
      : getFundPrice(investmentData, holding.fundId);
    const value = currentPrice * holding.units;

    if (distribution[fund.type]) {
      distribution[fund.type].value += value;
    }
  }

  // パーセンテージ計算
  for (const type of Object.keys(distribution)) {
    distribution[type].percent = totalValue > 0
      ? (distribution[type].value / totalValue) * 100
      : 0;
  }

  return distribution;
}

/**
 * 累計損益を計算
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} 累計損益情報
 */
export function getCumulativePnL(investmentData, marketIndex = null, marketState = null) {
  const totalStats = investmentData.totalStats || {
    totalProfit: 0,
    totalFeesPaid: 0,
    totalInvestments: 0,
    totalProfitableTrades: 0,
  };
  const realizedPnL = totalStats.totalProfit || 0;

  // 含み損益
  const { totalPnl: unrealizedPnL } = getTotalPortfolioValue(investmentData, marketIndex, marketState);

  // 手数料・維持コスト
  const totalFees = (totalStats.totalFeesPaid || 0) + (investmentData.totalMaintenancePaid || 0);

  // 純損益
  const netPnL = realizedPnL + unrealizedPnL - totalFees;

  return {
    realizedPnL,
    unrealizedPnL,
    totalFees,
    netPnL,
    totalInvestments: totalStats.totalInvestments || 0,
    winRate: totalStats.totalInvestments > 0
      ? ((totalStats.totalProfitableTrades || 0) / totalStats.totalInvestments) * 100
      : 0,
  };
}

// ========================================
// レバレッジ解放状況
// ========================================

/**
 * 解放済みレバレッジオプションを取得
 * @param {Object} investmentData - 投資データ
 * @returns {Array<number>} 解放済みレバレッジ倍率
 */
export function getUnlockedLeverages(investmentData) {
  const experience = investmentData.experienceCount;
  return INVESTMENT.LEVERAGE_OPTIONS.filter((lev) => {
    const required = INVESTMENT.LEVERAGE_UNLOCK[lev];
    return required !== undefined && experience >= required;
  });
}

/**
 * 次のレバレッジ解放までの情報を取得
 * @param {Object} investmentData - 投資データ
 * @returns {Object|null} { nextLeverage, required, remaining }
 */
export function getNextLeverageUnlock(investmentData) {
  const experience = investmentData.experienceCount;

  for (const lev of INVESTMENT.LEVERAGE_OPTIONS) {
    const required = INVESTMENT.LEVERAGE_UNLOCK[lev];
    if (required !== undefined && experience < required) {
      return {
        nextLeverage: lev,
        required,
        remaining: required - experience,
      };
    }
  }

  return null; // 全て解放済み
}

export default {
  getPortfolioSummary,
  getHoldingsList,
  sortHoldings,
  getAvailableFunds,
  getTradeHistory,
  enrichTradeHistory,
  getAttributeDistribution,
  getTypeDistribution,
  getCumulativePnL,
  getUnlockedLeverages,
  getNextLeverageUnlock,
};
